import React, { useMemo, useState } from 'react'
import {
  SelectableCard,
  SelectableCardGrid,
  SELECTABLE_CARD_DEMO_META,
  SELECTABLE_CARD_DEMO_TAGS,
  SELECTABLE_CARD_PREVIEW_STATE_LABELS,
  SELECTABLE_CARD_PREVIEW_STATES,
  type SelectableCardDensity,
  type SelectableCardPreviewState,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './SelectableCardShowcase.css'

const FAVOURITE_ITEMS = [
  {
    id: '1',
    title: 'Periodic oral evaluation (B1234)',
    subtitle: 'D0120',
  },
  {
    id: '2',
    title: 'Prophylaxis — adult (B1110)',
    subtitle: 'D1110',
  },
  {
    id: '3',
    title: 'Bitewings — four films (B0274)',
    subtitle: 'D0274',
  },
  {
    id: '4',
    title: 'Comprehensive oral evaluation (B0150)',
    subtitle: 'D0150',
  },
]

const BUNDLE_ITEMS = [
  {
    id: 'b1',
    title: 'New patient exam',
    tags: ['D0150', 'D0210', 'D1110'],
    meta: 'Created on 3/12/2026',
  },
  {
    id: 'b2',
    title: 'Hygiene recall',
    tags: ['D0120', 'D1110', 'D0274'],
    meta: 'Created on 2/8/2026',
  },
]

const SelectableCardShowcase: React.FC = () => {
  const [title, setTitle] = useState('Periodic oral evaluation (B1234)')
  const [subtitle, setSubtitle] = useState('D0120')
  const [density, setDensity] = useState<SelectableCardDensity>('compact')
  const [selected, setSelected] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [showEmptyFavourites, setShowEmptyFavourites] = useState(false)
  const [highlightedState, setHighlightedState] =
    useState<SelectableCardPreviewState>('default')

  const toggleFavourite = (id: string): void => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    )
  }

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'selectable-card-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="selectable-card-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as SelectableCardPreviewState,
                  )
                }
              >
                {SELECTABLE_CARD_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {SELECTABLE_CARD_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'selectable-card-title',
            label: 'Title',
            control: (
              <input
                id="selectable-card-title"
                className="ui-field-control"
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            ),
          },
          {
            id: 'selectable-card-subtitle',
            label: 'Subtitle',
            control: (
              <input
                id="selectable-card-subtitle"
                className="ui-field-control"
                type="text"
                value={subtitle}
                onChange={(event) => setSubtitle(event.target.value)}
              />
            ),
          },
          {
            id: 'selectable-card-density',
            label: 'Density',
            control: (
              <select
                id="selectable-card-density"
                className="ui-field-control"
                value={density}
                onChange={(event) =>
                  setDensity(event.target.value as SelectableCardDensity)
                }
              >
                <option value="compact">Compact (select)</option>
                <option value="comfortable">Comfortable (manage)</option>
              </select>
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'selectable-card-selected',
            label: 'Selected',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="selectable-card-selected"
                  type="checkbox"
                  checked={selected}
                  onChange={(event) => setSelected(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'selectable-card-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="selectable-card-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'selectable-card-actions',
            label: 'Actions',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="selectable-card-actions"
                  type="checkbox"
                  checked={showActions}
                  onChange={(event) => setShowActions(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'selectable-card-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="selectable-card-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'selectable-card-empty-favourites',
            label: 'Empty favourites',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="selectable-card-empty-favourites"
                  type="checkbox"
                  checked={showEmptyFavourites}
                  onChange={(event) =>
                    setShowEmptyFavourites(event.target.checked)
                  }
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [
      darkMode,
      density,
      disabled,
      highlightedState,
      selected,
      showActions,
      showEmptyFavourites,
      subtitle,
      title,
    ],
  )

  const actionButtons = (
    <>
      <button
        type="button"
        className="ds-selectable-card__action"
        aria-label="Edit"
      >
        <i className="mgc mgc_pencil_fill" aria-hidden="true" />
      </button>
      <button
        type="button"
        className="ds-selectable-card__action ds-selectable-card__action--danger"
        aria-label="Delete"
      >
        <i className="mgc mgc_delete_2_line" aria-hidden="true" />
      </button>
    </>
  )

  const shouldShowActions =
    showActions ||
    highlightedState === 'with-actions' ||
    highlightedState === 'with-meta'

  const liveDensity: SelectableCardDensity =
    shouldShowActions || highlightedState === 'with-tags'
      ? 'comfortable'
      : density

  const liveTags =
    highlightedState === 'with-tags' || highlightedState === 'with-meta'
      ? SELECTABLE_CARD_DEMO_TAGS
      : undefined

  const liveMeta =
    highlightedState === 'with-meta' ? SELECTABLE_CARD_DEMO_META : undefined

  return (
    <>
      <UiPreviewCanvas
        title="Selectable card"
        description="Catalog card for Favourites & Bundles — select mode (Appointment/Charting) or manage mode (Settings) via density and actions."
        stageClassName="ui-preview__stage--wide selectable-card-showcase__stage-wrap"
      >
        <div
          className={`selectable-card-showcase__sheet${
            darkMode ? ' selectable-card-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Property controls and highlighted state apply here. Toggle Actions
              to show edit/delete. Grid below mimics Favourites select and
              Bundles manage.
            </p>
            <div className="selectable-card-showcase__live-single">
              <SelectableCard
                title={title}
                subtitle={
                  highlightedState === 'with-tags' ? undefined : subtitle
                }
                tags={liveTags}
                meta={liveMeta}
                density={liveDensity}
                selected={selected || highlightedState === 'selected'}
                disabled={disabled || highlightedState === 'disabled'}
                actions={shouldShowActions ? actionButtons : null}
                darkMode={darkMode}
                onClick={
                  disabled || highlightedState === 'disabled'
                    ? undefined
                    : () => setSelected((value) => !value)
                }
              />
            </div>
          </section>

          <section className="selectable-card-showcase__demo">
            <h3 className="ui-showcase-live__title">Favourites (select)</h3>
            <p className="ui-showcase-live__note">
              Toggle Empty favourites to see the grid empty slot.
            </p>
            <SelectableCardGrid
              maxHeight={120}
              darkMode={darkMode}
              emptyState={{
                title: 'No favourites to display',
                description: "There's no data to show here",
              }}
            >
              {showEmptyFavourites
                ? null
                : FAVOURITE_ITEMS.map((item) => (
                    <SelectableCard
                      key={item.id}
                      title={item.title}
                      subtitle={item.subtitle}
                      density="compact"
                      selected={selectedIds.includes(item.id)}
                      darkMode={darkMode}
                      onClick={() => toggleFavourite(item.id)}
                    />
                  ))}
            </SelectableCardGrid>
          </section>

          <section className="selectable-card-showcase__demo">
            <h3 className="ui-showcase-live__title">Bundles (manage)</h3>
            <SelectableCardGrid darkMode={darkMode}>
              {BUNDLE_ITEMS.map((item) => (
                <SelectableCard
                  key={item.id}
                  title={item.title}
                  tags={item.tags}
                  meta={item.meta}
                  density="comfortable"
                  darkMode={darkMode}
                  actions={
                    <>
                      <button
                        type="button"
                        className="ds-selectable-card__action"
                        aria-label={`Edit ${item.title}`}
                      >
                        <i className="mgc mgc_pencil_fill" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="ds-selectable-card__action ds-selectable-card__action--danger"
                        aria-label={`Delete ${item.title}`}
                      >
                        <i
                          className="mgc mgc_delete_2_line"
                          aria-hidden="true"
                        />
                      </button>
                    </>
                  }
                />
              ))}
            </SelectableCardGrid>
          </section>

          <section className="selectable-card-showcase__matrix">
            {SELECTABLE_CARD_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`selectable-card-showcase__row${
                  previewState === highlightedState
                    ? ' selectable-card-showcase__row--selected'
                    : ''
                }${darkMode ? ' selectable-card-showcase__row--dark' : ''}`}
              >
                <h4 className="selectable-card-showcase__label">
                  {SELECTABLE_CARD_PREVIEW_STATE_LABELS[previewState]}
                </h4>
                <SelectableCard
                  previewState={previewState}
                  darkMode={darkMode}
                />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Selectable card"
        sections={propertySections}
      />
    </>
  )
}

export default SelectableCardShowcase
